/**
 * @file legal-normalize.ts
 * @description Терпимое чтение ответов API документов (phase19.16, SB139–SB144).
 *
 * Кабинет выходит вместе с новым API, но на dev и в окне выкладки API может
 * ещё отдавать прежнюю форму (phase10.5.X: `version`, `isPublished`, без
 * `kind`, `status`, `usage`). Страница документов при этом должна
 * открываться без падений: прежний документ читается как «Текст», его
 * `version` — номер действующей редакции, предупреждения считаются здесь же
 * по правилам shared (`legalDocumentWarnings`).
 *
 * Новая форма узнаётся по полям `kind` и `status` и проходит как есть
 * (недостающие у карточки `usage` и `warnings` дополняются пустыми).
 */

import {
  legalDocumentStatus,
  legalDocumentWarnings,
  type LegalDocumentDto,
  type LegalDocumentListItemDto,
  type LegalDocumentUsageDto,
  type LegalLiveRevisionInfoDto,
} from "@chaiberi/shared";

import { safeLegalHref } from "./safe-legal-href";

/** Прежняя форма строки списка и карточки (до phase19.16). */
interface LegacyLegalDocument {
  id: string;
  slug: string;
  title: string;
  contentHtml?: string;
  version?: number;
  isPublished?: boolean;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: { id: string; displayName: string | null } | null;
}

const EMPTY_USAGE: LegalDocumentUsageDto = { menuItems: [], footerRows: { draft: false, published: false }, group: null };

/** Ответ уже в новой форме? */
function isNewShape(raw: object): boolean {
  return "kind" in raw && "status" in raw;
}

/** Дата последнего изменения прежнего документа. */
function legacyUpdatedAt(raw: LegacyLegalDocument): string {
  return raw.updatedAt ?? raw.createdAt ?? new Date(0).toISOString();
}

/** Общие поля прежнего документа в новой форме (= строка списка без `members`). */
function fromLegacy(raw: LegacyLegalDocument): Omit<LegalDocumentListItemDto, "members"> {
  const isPublished = raw.isPublished === true;
  // Прежняя версия опубликованного документа = номер действующей редакции.
  const live: LegalLiveRevisionInfoDto | null = isPublished
    ? {
        number: raw.version ?? 1,
        publishedAt: raw.publishedAt ?? legacyUpdatedAt(raw),
        publishedByName: raw.updatedBy?.displayName ?? "",
      }
    : null;
  const state = { kind: "TEXT" as const, isPublished, liveRevisionId: live ? "legacy" : null, hasDraftChanges: false };
  return {
    id: raw.id,
    slug: raw.slug,
    kind: "TEXT",
    title: raw.title,
    // Адрес прежнего документа мог быть не по нынешнему правилу — не падаем.
    href: safeLegalHref(raw.slug) ?? `/legal/${raw.slug}`,
    status: legalDocumentStatus(state),
    isPublished,
    hasDraftChanges: false,
    visibleOnSite: live !== null,
    live,
    file: null,
    group: null,
    groupSource: null,
    warnings: legalDocumentWarnings({ ...state, slug: raw.slug, description: null }),
  };
}

/** Строка списка: новая форма как есть, прежняя — пересобранная. */
export function normalizeLegalListItem(raw: object): LegalDocumentListItemDto {
  if (isNewShape(raw)) {
    const item = raw as LegalDocumentListItemDto;
    return { ...item, warnings: item.warnings ?? [] };
  }
  return { ...fromLegacy(raw as LegacyLegalDocument), members: null };
}

/** Карточка документа: новая форма с дополненными `usage`/`warnings`, прежняя — пересобранная. */
export function normalizeLegalDocument(raw: object): LegalDocumentDto {
  if (isNewShape(raw)) {
    const doc = raw as LegalDocumentDto;
    return { ...doc, usage: doc.usage ?? EMPTY_USAGE, warnings: doc.warnings ?? [] };
  }
  const legacy = raw as LegacyLegalDocument;
  const updatedAt = legacyUpdatedAt(legacy);
  return {
    ...fromLegacy(legacy),
    description: null,
    contentHtml: legacy.contentHtml ?? "",
    pdfDisplay: "INLINE",
    draftFile: null,
    groupPosition: null,
    version: legacy.version ?? 1,
    publishedAt: legacy.publishedAt ?? null,
    members: null,
    usage: EMPTY_USAGE,
    createdAt: legacy.createdAt ?? updatedAt,
    updatedAt,
    updatedBy: legacy.updatedBy ? { id: legacy.updatedBy.id, name: legacy.updatedBy.displayName ?? "" } : null,
  };
}
